<script setup lang="ts">
import gsap from 'gsap'
import { useWorkStore } from '~/stores/WorkStore'
import type { SectionWorkListRecord } from '~/types/datocms'

interface WorkPageFilterProps {
  categories: SectionWorkListRecord['categories']
  allWorksLabel?: string
  allWorksCountLabel?: string
  allReelsLabel?: string
  allGamesLabel?: string
}

withDefaults(defineProps<WorkPageFilterProps>(), {
  allWorksLabel: 'All',
  allWorksCountLabel: 'Count:',
  allReelsLabel: 'Reels',
  allGamesLabel: 'Our Games'
})

const store = useWorkStore()
const route = useRoute()

const isShowAllWorks = computed(() => (Object.keys(route.query).length === 0 ? true : false))
const isMounted = ref(false)
const ctxRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | null = null

onMounted(() => {
  isMounted.value = true
  if (ctxRef.value) {
    ctx = gsap.context((self) => {
      const fxTabs = self.selector?.('.fx-tab')

      if (fxTabs) {
        gsap.set([fxTabs], { scale: 0 })
        gsap.to([fxTabs], {
          scale: 1,
          ease: 'expo.out',
          stagger: 0.15,
          filter: 'invert(1)',
          duration: 1.2
        })
      }
    }, ctxRef.value as HTMLElement)
  }
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <div
    ref="ctxRef"
    class="-ml-4 mb-2 flex w-[calc(100%+2rem)] items-center border-[#D5D5D5] pb-4 lg:mb-10 lg:ml-0 lg:w-full lg:justify-between lg:border-b lg:pb-10"
    :class="isMounted ? 'visible' : 'invisible'"
  >
    <nav
      aria-label="Tabs"
      data-lenis-prevent
      class="flex overflow-y-hidden overflow-x-scroll px-4 lg:gap-10 lg:px-0"
    >
      <WorkPageFilterItem
        class="fx-tab"
        :to="route.path"
        :is-active="isShowAllWorks"
        :is-disabled="store.isLoading"
      >
        {{ allWorksLabel }}</WorkPageFilterItem
      >
      <WorkPageFilterItem
        v-for="category in categories"
        :key="category.slug!"
        :to="{ path: route.path, query: { category: category.slug ?? '' } }"
        :is-active="route.query.category === category.slug"
        :is-disabled="store.isLoading"
        class="fx-tab"
        >{{ category.name }}</WorkPageFilterItem
      >
      <WorkPageFilterItem
        class="fx-tab"
        :is-disabled="store.isLoading"
        :is-active="route.query.category === 'reel'"
        :to="{ path: route.path, query: { category: 'reel' } }"
      >
        {{ allReelsLabel }}</WorkPageFilterItem
      >
      <WorkPageFilterItem
        class="fx-tab lg:hidden"
        :is-disabled="store.isLoading"
        :to="{ path: '/games' }"
      >
        {{ allGamesLabel }}</WorkPageFilterItem
      >
    </nav>
    <div class="hidden lg:block">
      <WorkPageFilterItem class="fx-tab" :is-disabled="store.isLoading" :to="{ path: '/games' }">
        {{ allGamesLabel }}</WorkPageFilterItem
      >
    </div>
  </div>
</template>
